/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { registerBrick } from 'olympe';
import { ReactBrick, useProperty, jsonToSxProps, cssToSxProps, ifNotTransparent, ifNotNull } from '@olympeio/core';

import React, {useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import GetVideoStream from "./GetVideoStream";

/**
 * Provide a Rectangle visual component (empty div) using MUI Box
 */
export default class Video extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return $.observe('Hidden');
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {

            const videoRef = useRef(null);
            const videoSrc = useProperty($, 'Video Stream', false);
            const objectFit = useProperty($, 'Image Fit');
            const camera = useProperty($, 'use camera in stream');
            useEffect(() => {
                if(!videoRef.current) return;

                if (videoSrc) {
                    videoRef.current.srcObject = videoSrc;
                }
            }, [videoSrc]);

            useEffect(() => {
                if (camera) {
                    GetVideoStream.initialize($, camera, $.set.bind($, 'Video Stream'));
                }
            }, [camera]);

            const [hidden] = props.values;
            const borderRadius = useProperty($, 'Border Radius');
            return !hidden && (
                <Box
                    // Properties + UI
                    component={'div'}
                    sx={{
                        width: 1,
                        height: 1,
                        ...ifNotTransparent('borderColor', useProperty($, 'Border Color')),
                        ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                        borderWidth: useProperty($, 'Border Width'),
                        borderStyle: 'solid',
                        boxSizing: 'border-box',
                        ...ifNotTransparent('backgroundColor', useProperty($, 'Default Color')),
                        ...cssToSxProps(useProperty($, 'CSS Property')),
                        ...jsonToSxProps(useProperty($, 'MUI sx [json]'))
                    }}

                    // Events
                    onClick={() => $.trigger('On Click')}
                >
                    <video ref={videoRef} autoPlay playsInline style={{width:'100%', height:'100%', objectFit: objectFit}}></video>
                </Box>
            );
        };
    }
}

registerBrick('018dcbd990873d060ab1', Video);
